/**
 * Billing Zod validation schemas.
 *
 * Authoritative frontend contract for billing server actions — mirrors
 * docs/openapiv.2.3.67.json and encodes UI-owned rules (R4, R5, R9, R10).
 */

import { z } from "zod";

import { ISO_COUNTRY_CODES } from "@/lib/constants/iso-countries";
import { STRIPE_TAX_ID_TYPE_VALUES } from "@/lib/constants/stripe-tax-id-types";
import { uuidSchema } from "@/lib/validation/schemas/common";

// =============================================================================
// Shared primitives
// =============================================================================

/** R4: ISO 3166-1 alpha-2 country code. */
export const isoCountrySchema = z
  .string()
  .length(2, "Country must be a 2-letter ISO code")
  .transform((v) => v.toUpperCase())
  .refine((v) => ISO_COUNTRY_CODES.has(v), {
    message: "Country must be a valid ISO 3166-1 alpha-2 code",
  });

/** R5: Stripe tax id type enum. */
export const stripeTaxIdTypeSchema = z
  .string()
  .refine((v) => STRIPE_TAX_ID_TYPE_VALUES.has(v), {
    message: "Tax ID type must be one of Stripe's allowed tax id types",
  });

const planSlugSchema = z.string().trim().min(1, "Plan is required").max(100);

const addonSlugSchema = z
  .string()
  .trim()
  .min(1, "Add-on is required")
  .max(100)
  .regex(
    /^[a-z0-9-]+$/,
    "Add-on slug must be lowercase alphanumeric with hyphens",
  );

// =============================================================================
// Billing info (R4, R5)
// =============================================================================

export const billingAddressSchema = z.object({
  line1: z.string().trim().min(1).max(200).nullish(),
  line2: z.string().trim().max(200).nullish(),
  city: z.string().trim().min(1).max(100).nullish(),
  state: z.string().trim().max(100).nullish(),
  postal_code: z.string().trim().max(20).nullish(),
  country: isoCountrySchema.nullish(),
});

export const billingInfoUpdateSchema = z
  .object({
    business_name: z.string().trim().max(150).nullish(),
    individual_name: z.string().trim().max(150).nullish(),
    email: z
      .string()
      .email("Please enter a valid email address")
      .max(255)
      .nullish(),
    phone: z.string().trim().max(20).nullish(),
    address: billingAddressSchema.nullish(),
    tax_id: z.string().trim().max(50).nullish(),
    tax_id_type: stripeTaxIdTypeSchema.nullish(),
  })
  .refine(
    (v) => {
      const hasId = !!v.tax_id && v.tax_id.trim() !== "";
      const hasType = !!v.tax_id_type && v.tax_id_type.trim() !== "";
      return hasId === hasType;
    },
    {
      message: "Tax ID and Tax ID type must be provided together",
      path: ["tax_id_type"],
    },
  );

/**
 * First-time billing info setup — enforces the minimum required fields Stripe
 * needs to create a customer and issue invoices.
 */
export const billingInfoRequiredSchema = z
  .object({
    business_name: z.string().trim().max(150).nullish(),
    individual_name: z.string().trim().max(150).nullish(),
    email: z
      .string()
      .trim()
      .min(1, "Billing email is required")
      .email("Please enter a valid email address")
      .max(255),
    phone: z.string().trim().max(20).nullish(),
    address: z.object({
      line1: z.string().trim().min(1, "Street address is required").max(200),
      line2: z.string().trim().max(200).nullish(),
      city: z.string().trim().min(1, "City is required").max(100),
      state: z.string().trim().max(100).nullish(),
      postal_code: z.string().trim().max(20).nullish(),
      country: isoCountrySchema,
    }),
    tax_id: z.string().trim().max(50).nullish(),
    tax_id_type: stripeTaxIdTypeSchema.nullish(),
  })
  .superRefine((v, ctx) => {
    const hasBusiness = !!v.business_name && v.business_name.trim() !== "";
    const hasIndividual =
      !!v.individual_name && v.individual_name.trim() !== "";
    if (!hasBusiness && !hasIndividual) {
      ctx.addIssue({
        code: "custom",
        path: ["business_name"],
        message: "Enter a business name or a contact name",
      });
    }
    const hasTaxId = !!v.tax_id && v.tax_id.trim() !== "";
    const hasTaxType = !!v.tax_id_type && v.tax_id_type.trim() !== "";
    if (hasTaxId !== hasTaxType) {
      ctx.addIssue({
        code: "custom",
        path: ["tax_id_type"],
        message: "Tax ID and Tax ID type must be provided together",
      });
    }
  });

// =============================================================================
// Subscription / checkout / add-locations (R10)
// =============================================================================

export const subscriptionLocationSelectionSchema = z.object({
  location_id: uuidSchema,
  plan_slug: planSlugSchema,
  addon_slugs: z.array(addonSlugSchema).max(20).optional(),
});

const locationsSelectionSchema = z
  .array(subscriptionLocationSelectionSchema)
  .min(1, "Select at least one location")
  .max(50, "Too many locations selected (max 50)");

export const checkoutRequestSchema = z.object({
  locations: locationsSelectionSchema,
  success_url: z.string().url("Invalid success URL"),
  cancel_url: z.string().url("Invalid cancel URL"),
});

export const subscriptionPreviewRequestSchema = z.object({
  locations: locationsSelectionSchema,
});

export const addLocationRequestSchema = z.object({
  locations: locationsSelectionSchema,
});

export const changeLocationPlanRequestSchema = z.object({
  plan_slug: planSlugSchema,
});

/** @deprecated Prefer changeLocationPlanRequestSchema. */
export const changePlanRequestSchema = changeLocationPlanRequestSchema;

export const attachLocationAddonsRequestSchema = z.object({
  addon_slugs: z
    .array(addonSlugSchema)
    .min(1, "Select at least one add-on")
    .max(20),
});

export const addonCheckoutItemSchema = z.object({
  addon_slug: addonSlugSchema,
  quantity: z.number().int().min(1).max(100).optional(),
});

export const addonCheckoutRequestSchema = z.object({
  items: z
    .array(addonCheckoutItemSchema)
    .min(1, "Select at least one add-on")
    .max(20),
  success_url: z.string().url("Invalid success URL"),
  cancel_url: z.string().url("Invalid cancel URL"),
});

// =============================================================================
// Invoices
// =============================================================================

export const invoiceListParamsSchema = z
  .object({
    year: z.number().int().min(2000).max(3000).optional(),
    month: z.number().int().min(1).max(12).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    starting_after: z.string().trim().min(1).optional(),
  })
  .refine((v) => !(v.month !== undefined && v.year === undefined), {
    message: "Year is required when month is provided",
    path: ["year"],
  });

// =============================================================================
// Usage events
// =============================================================================

export const usageEventTypeSchema = z.enum(["subscribed", "flex", "free"]);

export const usageEventListParamsSchema = z.object({
  event_name: z.string().trim().min(1).max(100).optional(),
  location_id: uuidSchema.optional(),
  usage_type: usageEventTypeSchema.optional(),
  is_paid: z.boolean().optional(),
  created_after: z.string().datetime({ offset: true }).optional(),
  created_before: z.string().datetime({ offset: true }).optional(),
  page: z.number().int().min(1).optional(),
  items_per_page: z.number().int().min(1).max(100).optional(),
});

// Re-exports for consumers
export type BillingInfoUpdateInput = z.infer<typeof billingInfoUpdateSchema>;
export type BillingInfoRequiredInput = z.infer<
  typeof billingInfoRequiredSchema
>;
export type SubscriptionLocationSelectionInput = z.infer<
  typeof subscriptionLocationSelectionSchema
>;
export type CheckoutRequestInput = z.infer<typeof checkoutRequestSchema>;
export type SubscriptionPreviewRequestInput = z.infer<
  typeof subscriptionPreviewRequestSchema
>;
export type AddLocationRequestInput = z.infer<typeof addLocationRequestSchema>;
export type ChangeLocationPlanRequestInput = z.infer<
  typeof changeLocationPlanRequestSchema
>;
export type ChangePlanRequestInput = ChangeLocationPlanRequestInput;
export type AttachLocationAddonsRequestInput = z.infer<
  typeof attachLocationAddonsRequestSchema
>;
export type AddonCheckoutRequestInput = z.infer<
  typeof addonCheckoutRequestSchema
>;
export type InvoiceListParamsInput = z.infer<typeof invoiceListParamsSchema>;
export type UsageEventListParamsInput = z.infer<
  typeof usageEventListParamsSchema
>;
